import { projectMembersListingConfiguration } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveProjectMembersListingConfigurationRequest(
  state: State,
  planzone: Boundaries["retrieveProjectMembersListingConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          configuration: projectMembersListingConfiguration,
        },
      ];
}

function handleRetrieveProjectMembersListingConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["retrieveProjectMembersListingConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleRetrieveProjectMembersListingConfigurationFailure(
  state: State,
  planzone: Boundaries["retrieveProjectMembersListingConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          configuration: projectMembersListingConfiguration,
        },
      ];
}

function handleEditProjectMembersListingConfigurationRequest(
  state: State,
  {
    planzone,
    nextConfiguration,
  }: Boundaries["editProjectMembersListingConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration: nextConfiguration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration: nextConfiguration,
        },
      ];
}

function handleEditProjectMembersListingConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editProjectMembersListingConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleEditProjectMembersListingConfigurationFailure(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editProjectMembersListingConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveProjectMembersListingConfigurationRequested]:
    handleRetrieveProjectMembersListingConfigurationRequest,
  [events.retrieveProjectMembersListingConfigurationSucceeded]:
    handleRetrieveProjectMembersListingConfigurationSuccess,
  [events.retrieveProjectMembersListingConfigurationFailed]:
    handleRetrieveProjectMembersListingConfigurationFailure,
  [events.editProjectMembersListingConfigurationRequested]:
    handleEditProjectMembersListingConfigurationRequest,
  [events.editProjectMembersListingConfigurationSucceeded]:
    handleEditProjectMembersListingConfigurationSuccess,
  [events.editProjectMembersListingConfigurationFailed]:
    handleEditProjectMembersListingConfigurationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
